'use client';

import React from 'react';
import {type AuthLoadingProps, LoadingState} from '../../../../types/auth';
import {Button} from '@/matic/components/ui/button';

/**
 * Skeleton loader for page-level loading states
 */
const SkeletonLoader: React.FC = () => (
  <div className="animate-pulse space-y-4 p-6">
    <div className="h-8 w-1/4 rounded bg-gray-200"></div>
    <div className="space-y-2">
      <div className="h-4 w-3/4 rounded bg-gray-200"></div>
      <div className="h-4 w-1/2 rounded bg-gray-200"></div>
      <div className="h-4 w-5/6 rounded bg-gray-200"></div>
    </div>
    <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="h-32 rounded bg-gray-200"></div>
      <div className="h-32 rounded bg-gray-200"></div>
    </div>
  </div>
);

/**
 * Authentication loading component with various states and accessibility features
 */
export const AuthLoading: React.FC<AuthLoadingProps> = ({
  state = 'loading',
  message = 'Loading authentication status...',
  showSkeleton = false,
  className = '',
}) => {
  const baseClasses = 'flex items-center justify-center min-h-[200px]';
  const combinedClasses = `${baseClasses} ${className}`;

  if (state === 'error') {
    return (
      <div
        className={combinedClasses}
        data-testid="auth-error"
        role="alert"
        aria-live="assertive"
      >
        <div className="space-y-4 text-center">
          <div className="text-lg font-medium text-red-600">
            Authentication Error
          </div>
          <p className="text-gray-600">
            There was a problem loading your authentication status.
          </p>
          <Button
            type="button"
            onClick={() => {
              globalThis.location.reload();
            }}
            variant="primary"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (showSkeleton) {
    return (
      <div
        data-testid="auth-loading"
        role="status"
        aria-live="polite"
        aria-label="Loading authentication status"
      >
        <SkeletonLoader />
      </div>
    );
  }

  // Return null — the persistent #app-loader in index.html covers the loading state
  return null;
};

export default AuthLoading;
